/** State + streaming cho chat AI Agent Hub (dùng chung bubble và trang chat). */
import { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'antd';
import { fetchHubChatUserContext } from '@/components/common/AiStepAssistant/adapters/knowledgeSteps';
import { aiAgentApi } from '@/services/knowledge/ai-agent-api';
import { AiHubStreamError } from '@/services/knowledge/ai-agent-stream';
import { agentRunClient, streamRun, toLegacyEvent } from '@/services/knowledge/agent-run-client';
import { usePrivateStore } from '@/store/usePrivateStore';
import type {
  AiHubChatLlmPrefs,
  AiHubChatRunMeta,
  AiHubCitation,
  AiHubEvent,
  AiHubPageContext,
  AiHubProvider,
} from '@/types/ai-agent';
import type { BscApplyOfferState } from './BscApplyOfferCard';
import type { ActionPlanAiPendingPlan, ActionPlanAiPendingState } from '@/types/action-plan-v2/ActionPlanAiPlan';
import type {
  ActionPlanAiTaskListPayload,
  ActionPlanAiTaskListState,
} from '@/types/action-plan-v2/ActionPlanAiTaskList';
import {
  buildB6ApplyOffer,
  detectBscApplyTarget,
  textsFromCandidates,
  type B1ApplyCandidate,
} from './bscApplyOffer';
import { chatDisplayText } from './chatDisplayText';
import { sanitizeAiAnswerText } from './sanitizeAiAnswer';

export interface ChatTimelineItem {
  key: string;
  label: string;
  status: 'running' | 'done' | 'error';
  detail?: string;
}

export interface ChatTurn {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  status: 'streaming' | 'completed' | 'failed' | 'cancelled';
  timeline: ChatTimelineItem[];
  citations: AiHubCitation[];
  error?: string;
  runId?: string;
  /** id tin nhắn trên engine (để chấm 👍/👎) + phiếu đã chấm */
  messageId?: string;
  feedback?: number | null;
  specialists?: string[];
  runMeta?: AiHubChatRunMeta;
  applyCandidates?: B1ApplyCandidate[];
  applyOffer?: BscApplyOfferState;
  actionPlanPending?: ActionPlanAiPendingState;
  actionPlanPendingQueue?: ActionPlanAiPendingState[];
  actionPlanTaskList?: ActionPlanAiTaskListState;
  actionPlanObjectiveList?: import('@/types/action-plan-v2/ActionPlanAiObjectiveList').ActionPlanAiObjectiveListState;
  actionPlanScopeList?: import('@/types/action-plan-v2/ActionPlanAiScopeList').ActionPlanAiScopeListState;
  actionPlanAssigneeList?: import('@/types/action-plan-v2/ActionPlanAiAssigneeList').ActionPlanAiAssigneeListState;
}

const AGENT_LABELS: Record<string, string> = {
  success_factor: 'Yếu tố thành công',
  company_strengths: 'Điểm mạnh công ty',
  competitors_list: 'Đối thủ cạnh tranh',
  competitive: 'Lợi thế cạnh tranh',
  swot: 'SWOT',
  strategy: 'Chiến lược',
  strategy_map: 'Bản đồ chiến lược',
  fishbone: 'Xương cá',
};

export const agentLabel = (id: string) => AGENT_LABELS[id] ?? id;

let localId = 0;
const nextId = () => `local-${Date.now()}-${++localId}`;

/** Bộ phận / giai đoạn đang chọn — engine dùng để đọc dữ liệu bước của công ty (nguồn `bsc`). */
const currentRequestHeader = (): Record<string, unknown> | undefined => {
  try {
    return usePrivateStore.getState().getRequestHeader() as unknown as Record<string, unknown>;
  } catch {
    return undefined;
  }
};

/** Run đang chạy theo phạm vi chat — tải lại trang thì nghe tiếp (run không bị huỷ khi rời trang). */
type ActiveRun = { runId: string; conversationId: string | null; prompt: string };
const activeRunKey = (scopeType?: string, scopeKey?: string) => `aihub:active-run:${scopeType ?? 'global'}:${scopeKey ?? ''}`;
const readActiveRun = (key: string): ActiveRun | null => {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as ActiveRun) : null;
  } catch {
    return null;
  }
};
const writeActiveRun = (key: string, run: ActiveRun | null) => {
  try {
    if (run) window.sessionStorage.setItem(key, JSON.stringify(run));
    else window.sessionStorage.removeItem(key);
  } catch {
    /* sessionStorage bị chặn: bỏ qua, chỉ mất khả năng nối lại */
  }
};

export interface ChatLlmConfig {
  provider: string;
  model: string;
  /** Chỉ RAM — không persist */
  apiKey: string;
  baseUrl: string;
  thinking: boolean;
  /** Temperature 0–2 */
  threshold: number;
}

export const DEFAULT_CHAT_LLM_CONFIG: ChatLlmConfig = {
  provider: 'ollama',
  model: '',
  apiKey: '',
  baseUrl: '',
  thinking: false,
  threshold: 0.2,
};

/** Sau khi lưu Cài đặt AI Agent — bubble/trang chat reload catalog + prefs. */
export const AIHUB_SETTINGS_UPDATED_EVENT = 'toppion:aihub-settings-updated';

const PREFS_SAVE_MS = 450;

const resolveChatLlmConfig = (
  prefs: AiHubChatLlmPrefs | null | undefined,
  tenant: { provider: string; model: string; base_url?: string | null },
  providers: AiHubProvider[],
  prev: ChatLlmConfig,
): ChatLlmConfig => {
  const configured = Boolean(prefs?.configured);
  const provider =
    (configured ? prefs?.provider?.trim() : '') ||
    tenant.provider?.trim() ||
    'ollama';
  const providerMeta = providers.find((p) => p.id === provider);
  const model =
    (configured ? prefs?.model?.trim() : '') ||
    tenant.model?.trim() ||
    providerMeta?.default_model?.trim() ||
    '';
  const baseUrl =
    (configured ? prefs?.base_url?.trim() : '') ||
    tenant.base_url?.trim() ||
    prev.baseUrl ||
    '';
  return {
    provider,
    model,
    apiKey: '',
    baseUrl,
    thinking: configured ? Boolean(prefs?.thinking) : prev.thinking,
    threshold:
      configured && typeof prefs?.threshold === 'number' ? prefs.threshold : prev.threshold,
  };
};

const prefsPayloadFromConfig = (cfg: ChatLlmConfig) => ({
  provider: cfg.provider.trim() || undefined,
  model: cfg.model.trim(),
  base_url: cfg.baseUrl.trim() || null,
  thinking: cfg.thinking,
  threshold: cfg.threshold,
});

export interface ChatLlmCatalog {
  providers: AiHubProvider[];
  tenantHasApiKey: boolean;
  tenantApiKeyMasked: string | null;
  tenantProvider: string;
  tenantModel: string;
}

const buildStreamLlmFields = (cfg: ChatLlmConfig, tenant: { provider: string; model: string; base_url?: string | null }) => {
  const body: {
    provider?: string;
    model?: string;
    api_key?: string;
    base_url?: string | null;
  } = {};
  const provider = cfg.provider.trim();
  const model = cfg.model.trim();
  if (provider && provider !== tenant.provider) body.provider = provider;
  else if (provider) body.provider = provider;
  if (model && model !== (tenant.model || '').trim()) body.model = model;
  else if (model) body.model = model;
  const apiKey = cfg.apiKey.trim();
  if (apiKey) body.api_key = apiKey;
  const baseUrl = cfg.baseUrl.trim();
  const tenantBase = (tenant.base_url || '').trim();
  if (baseUrl && baseUrl !== tenantBase) body.base_url = baseUrl;
  else if (baseUrl) body.base_url = baseUrl;
  return body;
};

export interface UseAiChatOptions {
  /** false (mặc định true): chưa dùng chat (vd. AI tắt ở Cấu hình / bubble không hiển thị) → không gọi API AI Hub nào lúc mount. */
  enabled?: boolean;
  pageContext?: AiHubPageContext;
  kbIds?: string[];
  scopeType?: 'global' | 'step';
  scopeKey?: string;
}

const buildApplyOffer = (
  userMessage: string,
  assistant: ChatTurn,
  page?: { step?: string; route?: string },
): BscApplyOfferState | undefined => {
  if (assistant.status !== 'completed') return undefined;
  const step = page?.step;
  if (!step) return undefined;
  if (step === 'b6') return buildB6ApplyOffer(assistant.applyCandidates ?? []);
  const agents = assistant.specialists ?? [];
  const target = detectBscApplyTarget(userMessage, agents, page);
  if (!target) return undefined;
  const texts = textsFromCandidates(
    assistant.applyCandidates ?? [],
    assistant.content,
    target.agents,
  );
  if (!texts.length) return undefined;
  return {
    section: target.section,
    label: target.label,
    texts,
    status: 'pending',
  };
};

export const useAiChat = (options: UseAiChatOptions = {}) => {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [streaming, setStreaming] = useState(false);
  const [lastPrompt, setLastPrompt] = useState('');
  const [llmConfig, setLlmConfigState] = useState<ChatLlmConfig>(DEFAULT_CHAT_LLM_CONFIG);
  const [llmCatalog, setLlmCatalog] = useState<ChatLlmCatalog | null>(null);
  const tenantSettingsRef = useRef<{ provider: string; model: string; base_url?: string | null }>({
    provider: 'ollama',
    model: '',
    base_url: null,
  });
  const abortRef = useRef<AbortController | null>(null);
  const runIdRef = useRef<string | null>(null);
  const stoppedRef = useRef(false);
  const budgetWarnedRef = useRef(false);
  const runKey = activeRunKey(options.scopeType, options.scopeKey);
  const lastPromptRef = useRef('');
  const llmConfigRef = useRef(llmConfig);
  llmConfigRef.current = llmConfig;
  const llmHydratedRef = useRef(false);
  const prefsSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const persistChatLlmPrefs = useCallback((cfg: ChatLlmConfig) => {
    if (prefsSaveTimerRef.current) clearTimeout(prefsSaveTimerRef.current);
    prefsSaveTimerRef.current = setTimeout(() => {
      void aiAgentApi.saveChatLlmPrefs(prefsPayloadFromConfig(cfg));
    }, PREFS_SAVE_MS);
  }, []);

  const setLlmConfig = useCallback(
    (next: ChatLlmConfig | ((prev: ChatLlmConfig) => ChatLlmConfig)) => {
      setLlmConfigState((prev) => {
        const value = typeof next === 'function' ? next(prev) : next;
        persistChatLlmPrefs(value);
        return value;
      });
    },
    [persistChatLlmPrefs],
  );

  const refreshLlmCatalog = useCallback(async (opts?: { rehydrate?: boolean }) => {
    // AI Hub chưa chạy / lỗi mạng → bỏ qua (không để unhandled rejection bật overlay dev).
    const settingsRes = await aiAgentApi.getSettings().catch(() => null);
    if (!settingsRes?.success) return false;
    const prefsRes = await aiAgentApi.getChatLlmPrefs().catch(() => null);
    const { settings, providers } = settingsRes.data;
    const prefs = prefsRes?.success ? prefsRes.data : null;
    tenantSettingsRef.current = {
      provider: settings.provider,
      model: settings.model || '',
      base_url: settings.base_url,
    };
    setLlmCatalog({
      providers,
      tenantHasApiKey: settings.has_api_key,
      tenantApiKeyMasked: settings.api_key_masked,
      tenantProvider: settings.provider,
      tenantModel: settings.model || '',
    });
    const rehydrate = opts?.rehydrate ?? !llmHydratedRef.current;
    if (rehydrate) {
      setLlmConfigState((prev) => resolveChatLlmConfig(prefs, tenantSettingsRef.current, providers, prev));
      llmHydratedRef.current = true;
    }
    return true;
  }, []);

  const chatEnabled = options.enabled !== false;
  useEffect(() => {
    if (!chatEnabled) return;
    void refreshLlmCatalog();
  }, [chatEnabled, refreshLlmCatalog]);

  useEffect(() => {
    if (!chatEnabled) return;
    const onSettingsUpdated = () => {
      void refreshLlmCatalog({ rehydrate: true });
    };
    window.addEventListener(AIHUB_SETTINGS_UPDATED_EVENT, onSettingsUpdated);
    return () => window.removeEventListener(AIHUB_SETTINGS_UPDATED_EVENT, onSettingsUpdated);
  }, [chatEnabled, refreshLlmCatalog]);

  useEffect(
    () => () => {
      if (prefsSaveTimerRef.current) clearTimeout(prefsSaveTimerRef.current);
    },
    [],
  );

  const patchAssistant = useCallback((id: string, update: (turn: ChatTurn) => ChatTurn) => {
    setTurns((prev) => prev.map((t) => (t.id === id ? update(t) : t)));
  }, []);

  const applyEvent = useCallback(
    (assistantId: string, event: AiHubEvent) => {
      const data = event.data as Record<string, unknown>;
      switch (event.type) {
        case 'conversation':
          setConversationId(String(data.conversation_id));
          break;
        case 'run.started':
          patchAssistant(assistantId, (t) => ({ ...t, runId: String(data.run_id) }));
          break;
        case 'budget.warning': {
          // Hạn mức token hôm nay (công ty / người dùng) đã dùng ≥ 80% — nhắc một lần mỗi phiên
          if (!budgetWarnedRef.current) {
            budgetWarnedRef.current = true;
            message.warning(`Đã dùng ${Number(data.used_pct ?? 80)}% hạn mức token AI hôm nay — liên hệ Admin nếu cần thêm.`, 6);
          }
          break;
        }
        case 'run.restarted':
          // Engine khởi động lại giữa chừng, instance khác chạy lại lượt này từ đầu → bỏ phần trả lời dở
          patchAssistant(assistantId, (t) => ({ ...t, runId: String(data.run_id), content: '', citations: [] }));
          break;
        case 'run.meta':
          patchAssistant(assistantId, (t) => ({
            ...t,
            runMeta: {
              ...(t.runMeta ?? {}),
              provider: data.provider != null ? String(data.provider) : t.runMeta?.provider,
              model: data.model != null ? String(data.model) : t.runMeta?.model,
              model_label: data.model_label != null ? String(data.model_label) : t.runMeta?.model_label,
              thinking: typeof data.thinking === 'boolean' ? data.thinking : t.runMeta?.thinking,
              threshold: typeof data.threshold === 'number' ? data.threshold : t.runMeta?.threshold,
            },
          }));
          break;
        case 'node.started':
          patchAssistant(assistantId, (t) => ({
            ...t,
            timeline: [...t.timeline, { key: `node-${data.node}`, label: String(data.label ?? data.node), status: 'running' }],
          }));
          break;
        case 'node.finished': {
          const specialists = (data.specialists as string[] | undefined) ?? [];
          patchAssistant(assistantId, (t) => ({
            ...t,
            specialists: specialists.length ? specialists : t.specialists,
            timeline: t.timeline.map((item) =>
              item.key === `node-${data.node}`
                ? {
                    ...item,
                    status: 'done',
                    detail: specialists.length ? `Hỏi chuyên gia: ${specialists.map(agentLabel).join(', ')}` : item.detail,
                  }
                : item,
            ),
          }));
          break;
        }
        case 'child.started':
          patchAssistant(assistantId, (t) => ({
            ...t,
            timeline: [
              ...t.timeline,
              { key: `child-${data.agent}`, label: `Chuyên gia ${agentLabel(String(data.agent))}`, status: 'running' },
            ],
          }));
          break;
        case 'child.finished': {
          const candidates = (data.candidates as B1ApplyCandidate[] | undefined) ?? [];
          const agentId = String(data.agent);
          patchAssistant(assistantId, (t) => {
            const agents = [...(t.runMeta?.agents ?? [])];
            const idx = agents.findIndex((a) => a.agent === agentId);
            const row = {
              agent: agentId,
              industry: data.industry != null ? chatDisplayText(String(data.industry), 40) : undefined,
              source: data.source != null ? String(data.source) : undefined,
              docs: typeof data.docs === 'number' ? data.docs : undefined,
              items: typeof data.items === 'number' ? data.items : undefined,
            };
            if (idx >= 0) agents[idx] = { ...agents[idx], ...row };
            else agents.push(row);
            const tools = [...(t.runMeta?.tools ?? [])];
            if (row.source === 'web' && !tools.includes('web_search')) tools.push('web_search');
            if (agentId === 'success_factor' && (row.docs ?? 0) > 0 && !tools.includes('success_factor.csv')) {
              tools.push('success_factor.csv');
            }
            return {
              ...t,
              applyCandidates: [...(t.applyCandidates ?? []), ...candidates],
              runMeta: { ...(t.runMeta ?? {}), agents, tools },
              timeline: t.timeline.map((item) =>
                item.key === `child-${data.agent}`
                  ? {
                      ...item,
                      status: data.ok ? 'done' : 'error',
                      detail: data.ok
                        ? [
                            `${data.items ?? 0} ý`,
                            row.industry ? `ngành ${chatDisplayText(row.industry, 36)}` : '',
                            typeof row.docs === 'number' ? `${row.docs} dòng nội bộ` : '',
                            row.source === 'web' ? 'web' : '',
                          ]
                            .filter(Boolean)
                            .join(' · ')
                        : String(data.error ?? ''),
                    }
                  : item,
              ),
            };
          });
          break;
        }
        case 'tool.call': {
          const toolName = String(data.tool ?? 'tool');
          const conn = data.connection != null ? String(data.connection) : '';
          const label = conn ? `MCP · ${conn}/${toolName}` : toolName;
          patchAssistant(assistantId, (t) => {
            const tools = [...(t.runMeta?.tools ?? [])];
            if (!tools.includes(label)) tools.push(label);
            return {
              ...t,
              runMeta: { ...(t.runMeta ?? {}), tools },
              timeline: [
                ...t.timeline,
                { key: `tool-${tools.length}-${toolName}`, label: `Tool: ${toolName}`, status: 'running' as const },
              ],
            };
          });
          break;
        }
        case 'tool.result': {
          const toolName = String(data.tool ?? '');
          patchAssistant(assistantId, (t) => {
            let closed = false;
            return {
              ...t,
              timeline: t.timeline.map((item) => {
                if (closed || item.status !== 'running' || !item.key.startsWith('tool-')) return item;
                if (item.label !== `Tool: ${toolName}`) return item;
                closed = true;
                return {
                  ...item,
                  status: data.ok ? 'done' : 'error',
                  detail: data.ok ? `${data.chars ?? 0} ký tự` : String(data.error ?? ''),
                };
              }),
            };
          });
          break;
        }
        case 'kb.hits': {
          const sources = (data.sources as string[] | undefined) ?? [];
          patchAssistant(assistantId, (t) => ({
            ...t,
            runMeta: {
              ...(t.runMeta ?? {}),
              kb_files: sources.length ? sources : t.runMeta?.kb_files,
            },
            timeline: [...t.timeline, { key: 'kb', label: `Tra tài liệu (${data.count ?? 0} đoạn)`, status: 'done' }],
          }));
          break;
        }
        case 'token':
          patchAssistant(assistantId, (t) => ({ ...t, content: t.content + String(data.text ?? '') }));
          break;
        case 'message': {
          const meta = (data.meta as AiHubChatRunMeta | undefined) ?? undefined;
          patchAssistant(assistantId, (t) => ({
            ...t,
            messageId: data.message_id ? String(data.message_id) : t.messageId,
            citations: (data.citations as AiHubCitation[]) ?? [],
            specialists: meta?.specialists?.length ? meta.specialists : t.specialists,
            runMeta: meta ? { ...(t.runMeta ?? {}), ...meta } : t.runMeta,
          }));
          break;
        }
        case 'page_append': {
          // Chế độ «đưa ra màn chính»: trợ lý bước đã chọn nhóm + nội dung → thẻ «Thêm vào trang» (không ghi DB)
          const texts = ((data.texts as string[] | undefined) ?? []).filter(Boolean);
          if (!texts.length || !data.section) break;
          patchAssistant(assistantId, (t) => ({
            ...t,
            applyOffer: {
              section: data.section as BscApplyOfferState['section'],
              label: String(data.label ?? ''),
              texts,
              extra: (data.extra as Record<string, unknown> | undefined) ?? undefined,
              status: 'pending',
            },
          }));
          break;
        }
        case 'apply_offer': {
          const specialists = (data.specialists as string[] | undefined) ?? [];
          const candidates = (data.candidates as B1ApplyCandidate[] | undefined) ?? [];
          patchAssistant(assistantId, (t) => {
            const merged: ChatTurn = {
              ...t,
              specialists: specialists.length ? specialists : t.specialists,
              applyCandidates: candidates.length ? candidates : t.applyCandidates,
            };
            if (merged.applyOffer) return merged; // đã có thẻ từ trợ lý bước (page_append)
            const offer = buildApplyOffer(lastPromptRef.current, { ...merged, status: 'completed' }, options.pageContext);
            return offer ? { ...merged, applyOffer: offer } : merged;
          });
          break;
        }
        case 'pending_action': {
          const plan = data.plan as ActionPlanAiPendingPlan | undefined;
          if (!plan) break;
          const entry: ActionPlanAiPendingState = {
            plan,
            status: 'pending',
            runId: typeof data.run_id === 'string' ? data.run_id : undefined,
            interruptId: typeof data.interrupt_id === 'string' ? data.interrupt_id : undefined,
          };
          patchAssistant(assistantId, (t) => {
            const cur = t.actionPlanPending;
            const curActive =
              cur && (cur.status === 'pending' || cur.status === 'confirming');
            if (curActive) {
              return {
                ...t,
                actionPlanPendingQueue: [...(t.actionPlanPendingQueue ?? []), entry],
              };
            }
            return { ...t, actionPlanPending: entry };
          });
          break;
        }
        case 'objective_list': {
          const list = data.list as import('@/types/action-plan-v2/ActionPlanAiObjectiveList').ActionPlanAiObjectiveListPayload | undefined;
          if (!list?.items) break;
          patchAssistant(assistantId, (t) => ({
            ...t,
            actionPlanObjectiveList: { list },
          }));
          break;
        }
        case 'scope_list': {
          const list = data.list as import('@/types/action-plan-v2/ActionPlanAiScopeList').ActionPlanAiScopeListPayload | undefined;
          if (!list?.phongBans) break;
          patchAssistant(assistantId, (t) => ({
            ...t,
            actionPlanScopeList: { list },
          }));
          break;
        }
        case 'assignee_list': {
          const list = data.list as import('@/types/action-plan-v2/ActionPlanAiAssigneeList').ActionPlanAiAssigneeListPayload | undefined;
          if (!list?.nhanViens) break;
          patchAssistant(assistantId, (t) => ({
            ...t,
            actionPlanAssigneeList: { list },
          }));
          break;
        }
        case 'task_list': {
          const list = data.list as ActionPlanAiTaskListPayload | undefined;
          if (!list?.items) break;
          patchAssistant(assistantId, (t) => ({
            ...t,
            actionPlanTaskList: { list },
          }));
          break;
        }
        case 'error':
          patchAssistant(assistantId, (t) => ({ ...t, status: 'failed', error: String(data.message ?? 'Lỗi') }));
          break;
        case 'run.finished':
          patchAssistant(assistantId, (t) => {
            const next: ChatTurn = {
              ...t,
              status: data.status === 'completed' ? 'completed' : data.status === 'cancelled' ? 'cancelled' : 'failed',
              error: data.error ? String(data.error) : t.error,
              content: sanitizeAiAnswerText(t.content),
              timeline: t.timeline.map((item) => (item.status === 'running' ? { ...item, status: 'done' } : item)),
              actionPlanPending: t.actionPlanPending,
            };
            if (next.status === 'completed' && !next.applyOffer) {
              const offer = buildApplyOffer(lastPromptRef.current, next, options.pageContext);
              if (offer) next.applyOffer = offer;
            }
            return next;
          });
          break;
        default:
          break;
      }
    },
    [options.pageContext, patchAssistant],
  );

  const send = useCallback(
    async (message: string) => {
      const text = message.trim();
      if (!text || streaming) return;
      setLastPrompt(text);
      lastPromptRef.current = text;
      const assistantId = nextId();
      setTurns((prev) => [
        ...prev,
        { id: nextId(), role: 'user', content: text, status: 'completed', timeline: [], citations: [] },
        { id: assistantId, role: 'assistant', content: '', status: 'streaming', timeline: [], citations: [] },
      ]);
      const controller = new AbortController();
      abortRef.current = controller;
      stoppedRef.current = false;
      setStreaming(true);
      try {
        const cfg = llmConfigRef.current;
        let userContext: Record<string, unknown> | undefined;
        if (options.pageContext?.step) {
          try {
            userContext = await fetchHubChatUserContext(options.pageContext);
          } catch {
            userContext = undefined;
          }
        }
        const llmOverrides = buildStreamLlmFields(cfg, tenantSettingsRef.current);
        await streamRun(
          {
            kind: 'chat',
            input: text,
            conversation_id: conversationId,
            scope_type: options.scopeType ?? 'global',
            scope_key: options.scopeKey ?? '',
            page_context: options.pageContext ? { ...options.pageContext, requestHeader: currentRequestHeader() } : undefined,
            kb_ids: options.kbIds ?? [],
            user_context: userContext,
            thinking: cfg.thinking,
            threshold: cfg.threshold,
            ...llmOverrides,
          },
          {
            signal: controller.signal,
            onEvent: (event) => applyEvent(assistantId, event),
            onRunStarted: ({ run_id, conversation_id }) => {
              runIdRef.current = run_id;
              writeActiveRun(runKey, { runId: run_id, conversationId: conversation_id, prompt: text });
            },
          },
        );
        writeActiveRun(runKey, null);
      } catch (error) {
        if (stoppedRef.current) writeActiveRun(runKey, null);
        if (controller.signal.aborted) {
          patchAssistant(assistantId, (t) => ({ ...t, status: 'cancelled' }));
        } else {
          const msg = error instanceof AiHubStreamError ? error.message : 'Không kết nối được trợ lý AI.';
          patchAssistant(assistantId, (t) => ({ ...t, status: 'failed', error: msg }));
        }
      } finally {
        abortRef.current = null;
        runIdRef.current = null;
        setStreaming(false);
      }
    },
    [applyEvent, conversationId, options.kbIds, options.pageContext, options.scopeKey, options.scopeType, patchAssistant, runKey, streaming],
  );

  // Tải lại trang khi đang trả lời: nghe tiếp run cũ (engine vẫn chạy, phát lại cả phần đã trả lời)
  useEffect(() => {
    const active = readActiveRun(runKey);
    if (!active) return undefined;
    const assistantId = nextId();
    setTurns((prev) => [
      ...prev,
      { id: nextId(), role: 'user', content: active.prompt, status: 'completed', timeline: [], citations: [] },
      { id: assistantId, role: 'assistant', content: '', status: 'streaming', timeline: [], citations: [] },
    ]);
    if (active.conversationId) setConversationId(active.conversationId);
    const controller = new AbortController();
    abortRef.current = controller;
    runIdRef.current = active.runId;
    setStreaming(true);
    void agentRunClient
      .events(active.runId, { signal: controller.signal, onEvent: (e) => applyEvent(assistantId, toLegacyEvent(e)) })
      .then(() => writeActiveRun(runKey, null))
      .catch((error) => {
        if (controller.signal.aborted) return;
        writeActiveRun(runKey, null);
        const msg = error instanceof AiHubStreamError ? error.message : 'Không nối lại được trợ lý AI.';
        patchAssistant(assistantId, (t) => ({ ...t, status: 'failed', error: msg }));
      })
      .finally(() => {
        if (abortRef.current === controller) {
          abortRef.current = null;
          runIdRef.current = null;
          setStreaming(false);
        }
      });
    return () => controller.abort();
    // Chỉ chạy khi mở hook / đổi phạm vi chat
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runKey]);

  /** Dừng: huỷ run trên engine (ngắt kết nối không còn huỷ run) rồi ngừng nghe. */
  const stop = useCallback(() => {
    stoppedRef.current = true;
    if (runIdRef.current) void agentRunClient.cancel(runIdRef.current).catch(() => undefined);
    abortRef.current?.abort();
  }, []);

  const reset = useCallback(() => {
    stoppedRef.current = true;
    if (runIdRef.current) void agentRunClient.cancel(runIdRef.current).catch(() => undefined);
    writeActiveRun(runKey, null);
    abortRef.current?.abort();
    setTurns([]);
    setConversationId(null);
  }, [runKey]);

  const updateApplyOffer = useCallback((turnId: string, offer: BscApplyOfferState) => {
    setTurns((prev) => prev.map((t) => (t.id === turnId ? { ...t, applyOffer: offer } : t)));
  }, []);

  const updateActionPlanPending = useCallback((turnId: string, pending: ActionPlanAiPendingState) => {
    setTurns((prev) => prev.map((t) => (t.id === turnId ? { ...t, actionPlanPending: pending } : t)));
  }, []);

  const updateActionPlanAssigneeList = useCallback(
    (turnId: string, state: import('@/types/action-plan-v2/ActionPlanAiAssigneeList').ActionPlanAiAssigneeListState) => {
      setTurns((prev) => prev.map((t) => (t.id === turnId ? { ...t, actionPlanAssigneeList: state } : t)));
    },
    [],
  );

  const updateActionPlanTaskList = useCallback((turnId: string, state: ActionPlanAiTaskListState) => {
    setTurns((prev) => prev.map((t) => (t.id === turnId ? { ...t, actionPlanTaskList: state } : t)));
  }, []);

  const advanceActionPlanPendingQueue = useCallback((turnId: string) => {
    setTurns((prev) =>
      prev.map((t) => {
        if (t.id !== turnId) return t;
        const queue = t.actionPlanPendingQueue ?? [];
        if (!queue.length) return t;
        return {
          ...t,
          actionPlanPending: queue[0],
          actionPlanPendingQueue: queue.slice(1),
        };
      }),
    );
  }, []);

  const loadConversation = useCallback(async (id: string) => {
    const res = await aiAgentApi.messages(id);
    if (!res?.success) return;
    setConversationId(id);
    setTurns(
      res.data
        .filter((m) => m.role !== 'tool')
        .map((m) => ({
          id: m.id,
          role: m.role as 'user' | 'assistant',
          content: m.role === 'assistant' ? sanitizeAiAnswerText(m.content) : m.content,
          status: m.status === 'streaming' || m.status === 'pending' ? 'completed' : m.status,
          timeline: [],
          citations: m.citations ?? [],
          error: m.error ?? undefined,
          runId: m.run_id ?? undefined,
          messageId: m.role === 'assistant' ? m.id : undefined,
          feedback: m.feedback ?? null,
        })),
    );
  }, []);

  return {
    turns,
    conversationId,
    streaming,
    lastPrompt,
    llmConfig,
    setLlmConfig,
    llmCatalog,
    refreshLlmCatalog,
    send,
    stop,
    reset,
    loadConversation,
    updateApplyOffer,
    updateActionPlanPending,
    updateActionPlanTaskList,
    updateActionPlanAssigneeList,
    advanceActionPlanPendingQueue,
  };

};
